import {
  Row,
  Col,
  List,
  Button,
  Tooltip,
  Typography,
  Flex,
  Modal,
  Form,
  Input,
  Space,
  Skeleton,
  Empty,
  Popconfirm,
  Divider,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../../../styles/Configuration/SiteView.css";
import useFetch from "../../../hooks/useFetch";

const { Title } = Typography;

const sldForm = [
  {
    type: "text",
    name: "name",
    label: "Name",
    colspan: 24,
    rules: [{ required: true, message: "Name is required" }, { max: 3 }],
  },
];

export default function SLDView(props) {
  const navigate = useNavigate();
  const { siteID } = props;
  const [fetchWrapper] = useFetch();
  const [selectedSldID, setSelectedSldID] = useState(null);
  const [sldList, setSldList] = useState(undefined);
  const [openModel, setOpenModel] = useState(false);
  const [openEditModel, setOpenEditModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, editForm] = Form.useForm();

  const onFinish = () => {
    setLoading(true);
    addSld();
  };

  const onEditFinish = () => {
    setLoading(true);
    editSld();
  };

  function addSld() {
    fetchWrapper({
      endpoint_url: "single_line_diagram/addSingleLineDiagram",
      body: { ...form.getFieldsValue(), site_id: siteID },
      onSuccess: () => {
        getSldList();
      },
      onFinish: () => {
        form.resetFields();
        setOpenModel(false);
        setLoading(false);
      },
    });
  }

  function editSld() {
    fetchWrapper({
      endpoint_url: "single_line_diagram/editSingleLineDiagram",
      method: "PUT",
      params: { ID: selectedSldID },
      body: { ...form.getFieldsValue() },
      onSuccess: () => {
        getSldList();
      },
      onFinish: () => {
        form.resetFields();
        setOpenEditModel(false);
        setLoading(false);
      },
    });
  }

  function deleteSld(id) {
    fetchWrapper({
      endpoint_url: "single_line_diagram/deleteSingleLineDiagram",
      body: { ID: id },
      onSuccess: () => {
        getSldList();
      },
    });
  }

  function getSldList() {
    fetchWrapper({
      endpoint_url: "single_line_diagram/getSingleLineDiagramList",
      method: "GET",
      params: { SiteID: siteID },
      onSuccess: (value) => {
        setSldList(value.data);
      },
      onResponseError: (error) => {
        setSldList(false);
      },
    });
  }

  useEffect(() => {
    getSldList();
  }, []);

  return (
    <div className="list-view" style={{ height: "100%" }}>
      <Row gutter={20}>
        <Col xs={24} sm={24} md={24} lg={10} xl={10}>
          <div className="mb-2">
            <Title level={3} className="mt-0">
              Single Line Diagram
            </Title>
            <Button
              onClick={() => setOpenModel(true)}
              icon={<PlusOutlined />}
              className="mb-5 mt-3"
              style={{ width: "100%" }}
            >
              New Single Line Diagram
            </Button>
            {sldList == undefined ? (
              <Skeleton active paragraph={{ rows: 5 }} title={false}></Skeleton>
            ) : sldList == false ? (
              <>Failed to Load</>
            ) : sldList.length === 0 ? (
              <Empty className="mt-5" />
            ) : (
              <List
                dataSource={sldList}
                size="large"
                renderItem={(item) => (
                  <List.Item
                    className={
                      selectedSldID === item.ID
                        ? "ant-list-item list-item-selected"
                        : "ant-list-item list-item"
                    }
                    onClick={() => setSelectedSldID(item.ID)}
                    onDoubleClick={() => {
                      navigate(
                        `/Configuration/Site/SingleLineDiagram/${item.ID}`
                      );
                    }}
                  >
                    <Flex
                      justify="space-between"
                      style={{
                        width: "100%",
                      }}
                    >
                      <div>{item.Name}</div>
                      <Flex gap={8}>
                        <div className="mr-2">
                          <Tooltip title="Edit">
                            <EditOutlined
                              onClick={() => {
                                form.setFieldValue("name", item.Name);
                                setOpenEditModel(true);
                              }}
                              style={{
                                color:
                                  selectedSldID == item.ID
                                    ? "var(--ant-color-white)"
                                    : "var(--ant-color-primary)",
                              }}
                            />
                          </Tooltip>
                        </div>
                        <div>
                          <Tooltip title="Delete">
                            <Popconfirm
                              title="Delete"
                              description={
                                "Are you sure to delete this Single Line Diagram?"
                              }
                              onConfirm={() => {
                                deleteSld(item.ID);
                              }}
                              okText="Yes"
                              cancelText="No"
                            >
                              <DeleteOutlined
                                style={{
                                  color: "var(--ant-color-error)",
                                }}
                              />
                            </Popconfirm>
                          </Tooltip>
                        </div>
                      </Flex>
                    </Flex>
                  </List.Item>
                )}
              />
            )}
          </div>
        </Col>
      </Row>
      <Modal
        title="New Single Line Diagram"
        open={openModel}
        onCancel={() => setOpenModel(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className={"mt-5"}
        >
          <Divider className="mb-4 mt-0"></Divider>
          <Form.Item
            name={"name"}
            label={"Name"}
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input></Input>
          </Form.Item>
          <Flex justify="end" align="center" gap="middle">
            <Button
              type="default"
              onClick={() => {
                form.resetFields();
                setOpenModel(false);
              }}
            >
              Cancel
            </Button>
            <Button htmlType="submit" loading={loading} type="primary">
              Submit
            </Button>
          </Flex>
        </Form>
      </Modal>
      <Modal
        title="Edit Single Line Diagram"
        open={openEditModel}
        onCancel={() => setOpenEditModel(false)}
        footer={null}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <Form
          form={form}
          layout="vertical"
          onFinish={onEditFinish}
          className={"mt-5"}
        >
          <Form.Item
            name={"name"}
            label={"Name"}
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input></Input>
          </Form.Item>
          <Flex justify="end" align="center" gap="middle">
            <Button
              type="default"
              onClick={() => {
                form.resetFields();
                setOpenEditModel(false);
              }}
            >
              Cancel
            </Button>
            <Button htmlType="submit" loading={loading} type="primary">
              Submit
            </Button>
          </Flex>
        </Form>
      </Modal>
    </div>
  );
}
